import { useEffect, useMemo, useRef, useState } from "react";
import { Search, Users, Loader2, X } from "lucide-react";
import {
  useGetMailGroupsQuery,
  usePreviewAudienceMutation,
  pruneAudience,
  audienceIsEmpty,
  type Audience,
  type AudienceSummary,
  type GroupOption,
  type CampaignChannel,
} from "../api/mailApi";

/**
 * Picks who a mail-out goes to.
 *
 * Every group ticked is a union, de-duplicated server-side by email address, so
 * a Lagos Fellow who also sits on a committee gets one copy. The count under
 * the picker is the real resolved figure from the API — not a sum of the group
 * sizes, which would double-count exactly those people.
 */
type Props = {
  value: Audience;
  onChange: (next: Audience) => void;
  onSummary?: (summary: AudienceSummary | null) => void;
  disabled?: boolean;
  /**
   * Which address the count is for. A member with an email and no mobile is
   * in one audience and not the other, so the same groups give two different
   * totals and the picker must ask for the right one.
   */
  channel?: CampaignChannel;
};

/** A searchable checkbox list — the portal ships no multi-select component. */
function MultiSelect({
  label,
  options,
  selected,
  onToggle,
  disabled,
  emptyHint,
}: {
  label: string;
  options: GroupOption[];
  selected: string[];
  onToggle: (id: string) => void;
  disabled?: boolean;
  emptyHint?: string;
}) {
  const [term, setTerm] = useState("");

  const shown = useMemo(() => {
    const t = term.trim().toLowerCase();
    return t ? options.filter((o) => o.name?.toLowerCase().includes(t)) : options;
  }, [options, term]);

  return (
    <div className="border border-gray-100 rounded-2xl overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-100">
        <span className="text-[10px] font-black uppercase tracking-[0.15em] text-gray-500">
          {label}
        </span>
        {selected.length > 0 && (
          <span className="text-[10px] font-black text-indigo-600">{selected.length} selected</span>
        )}
      </div>

      {options.length > 8 && (
        <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-50">
          <Search className="w-3.5 h-3.5 text-gray-300" />
          <input
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder={`Search ${label.toLowerCase()}…`}
            className="w-full text-xs outline-none bg-transparent placeholder:text-gray-300"
          />
        </div>
      )}

      <div className="max-h-52 overflow-y-auto p-1.5">
        {shown.length === 0 && (
          <p className="px-3 py-4 text-xs text-gray-400">{emptyHint ?? "Nothing to choose from."}</p>
        )}
        {shown.map((o) => {
          const on = selected.includes(o.id);
          return (
            <label
              key={o.id}
              className={`flex items-center gap-2.5 px-3 py-2 rounded-xl cursor-pointer transition-colors ${
                on ? "bg-indigo-50" : "hover:bg-gray-50"
              } ${disabled ? "opacity-50 pointer-events-none" : ""}`}
            >
              <input
                type="checkbox"
                checked={on}
                onChange={() => onToggle(o.id)}
                className="w-3.5 h-3.5 accent-indigo-600"
              />
              <span className={`text-xs ${on ? "font-bold text-indigo-900" : "text-gray-700"}`}>
                {o.name}
              </span>
              {o.is_active && (
                <span className="text-[9px] font-black uppercase tracking-wider text-emerald-600">
                  active
                </span>
              )}
              {o.status && (
                <span className="text-[9px] font-black uppercase tracking-wider text-gray-400">
                  {o.status}
                </span>
              )}
            </label>
          );
        })}
      </div>
    </div>
  );
}

export default function AudiencePicker({ value, onChange, onSummary, disabled, channel }: Props) {
  const { data: groups, isLoading } = useGetMailGroupsQuery();
  const [preview, { isLoading: counting }] = usePreviewAudienceMutation();
  const [summary, setSummary] = useState<AudienceSummary | null>(null);
  // What this field last pushed up, so an incoming draft can be told apart from
  // the admin's own typing.
  const lastEmitted = useRef((value.emails ?? []).join("\n"));
  const [emailText, setEmailText] = useState(lastEmitted.current);
  const incomingEmails = (value.emails ?? []).join("\n");

  /**
   * How many registrations the chosen events hold at one payment level.
   *
   * Summed across the ticked events from the counts the options carry, so it is
   * a hint on the button rather than another round trip. It can read slightly
   * high against the final figure, which de-duplicates by address and drops
   * unmailable ones — the resolved count under the picker remains the one that
   * matters, and this only helps choose a level.
   */
  const eventLevelCount = (level: "paid" | "unpaid") => {
    const chosen = new Set(value.events ?? []);

    return (groups?.events ?? [])
      .filter((e) => chosen.has(e.id))
      .reduce((sum, e) => sum + Number(e[level] ?? 0), 0);
  };

  // The picker mounts before the draft has loaded, so the addresses saved on it
  // arrive after this field's initial value was read. Without this the box shows
  // empty and the next blur would wipe what was saved.
  useEffect(() => {
    if (incomingEmails !== lastEmitted.current) {
      lastEmitted.current = incomingEmails;
      setEmailText(incomingEmails);
    }
  }, [incomingEmails]);

  const pruned = useMemo(() => pruneAudience(value), [value]);
  const prunedKey = JSON.stringify(pruned);

  // Re-count whenever the selection settles. Debounced — ticking six chapters
  // in a row should cost one query, not six.
  useEffect(() => {
    if (audienceIsEmpty(value)) {
      setSummary(null);
      onSummary?.(null);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const result = await preview({ audience: pruned, channel }).unwrap();
        setSummary(result);
        onSummary?.(result);
      } catch {
        setSummary(null);
        onSummary?.(null);
      }
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // Channel included: flipping email↔SMS changes who is reachable, so the
    // total on screen must be recounted rather than left describing the other
    // channel's audience.
  }, [prunedKey, channel]);

  const toggleIn = (key: keyof Audience, id: string) => {
    const current = (value[key] as string[] | undefined) ?? [];
    const next = current.includes(id) ? current.filter((x) => x !== id) : [...current, id];
    onChange({ ...value, [key]: next });
  };

  /**
   * Commit on every keystroke, not on blur.
   *
   * Blur is too late: clicking Send fires blur and click close enough together
   * that the send can read the audience from before the address was added — and
   * when extra addresses are the ONLY selection, Send is still disabled at the
   * moment of the click. The head-count this feeds is already debounced, so
   * committing per keystroke costs nothing extra.
   */
  const commitEmails = (text: string) => {
    setEmailText(text);

    const list = text
      .split(/[\n,;]+/)
      .map((e) => e.trim())
      .filter(Boolean);

    lastEmitted.current = list.join("\n");
    onChange({ ...value, emails: list });
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-xs text-gray-400 p-6">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading groups…
      </div>
    );
  }

  const chairmenAll = value.chapter_chairmen === true;
  const executivesCurrent = value.executives === true;
  const npcAll = value.national_positions === true;

  return (
    <div className="space-y-4">
      {/* Institute-wide, and the two role groups that are a single tick. */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <button
          type="button"
          disabled={disabled}
          onClick={() => onChange({ ...value, all_members: !value.all_members })}
          className={`text-left px-4 py-3 rounded-2xl border transition-all ${
            value.all_members
              ? "bg-gray-900 text-white border-gray-900"
              : "bg-white border-gray-200 hover:border-gray-300"
          }`}
        >
          <p className="text-[10px] font-black uppercase tracking-[0.15em]">All members</p>
          <p className={`text-[10px] mt-1 ${value.all_members ? "text-gray-300" : "text-gray-400"}`}>
            Everyone on the register
          </p>
        </button>

        <button
          type="button"
          disabled={disabled}
          onClick={() => onChange({ ...value, executives: executivesCurrent ? [] : true })}
          className={`text-left px-4 py-3 rounded-2xl border transition-all ${
            executivesCurrent
              ? "bg-gray-900 text-white border-gray-900"
              : "bg-white border-gray-200 hover:border-gray-300"
          }`}
        >
          <p className="text-[10px] font-black uppercase tracking-[0.15em]">Executives</p>
          <p className={`text-[10px] mt-1 ${executivesCurrent ? "text-gray-300" : "text-gray-400"}`}>
            Whoever is in office now
          </p>
        </button>

        <button
          type="button"
          disabled={disabled}
          onClick={() => onChange({ ...value, chapter_chairmen: chairmenAll ? [] : true })}
          className={`text-left px-4 py-3 rounded-2xl border transition-all ${
            chairmenAll
              ? "bg-gray-900 text-white border-gray-900"
              : "bg-white border-gray-200 hover:border-gray-300"
          }`}
        >
          <p className="text-[10px] font-black uppercase tracking-[0.15em]">Chapter chairmen</p>
          <p className={`text-[10px] mt-1 ${chairmenAll ? "text-gray-300" : "text-gray-400"}`}>
            Every chapter's sitting chairman
          </p>
        </button>

        {/* The whole National Policy Committee in one tick — past presidents
            and secretaries, the trustees, the national body chairmen and the
            Institute's outside representatives. Only those SERVING now. */}
        <button
          type="button"
          disabled={disabled}
          onClick={() => onChange({ ...value, national_positions: npcAll ? [] : true })}
          className={`text-left px-4 py-3 rounded-2xl border transition-all ${
            npcAll
              ? "bg-gray-900 text-white border-gray-900"
              : "bg-white border-gray-200 hover:border-gray-300"
          }`}
        >
          <p className="text-[10px] font-black uppercase tracking-[0.15em]">Policy committee</p>
          <p className={`text-[10px] mt-1 ${npcAll ? "text-gray-300" : "text-gray-400"}`}>
            Everyone serving, plus the FCT Abuja and Lagos chapter chairmen
          </p>
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <MultiSelect
          label="Chapter"
          options={groups?.chapters ?? []}
          selected={value.chapters ?? []}
          onToggle={(id) => toggleIn("chapters", id)}
          disabled={disabled}
        />
        <MultiSelect
          label="Member category"
          options={groups?.categories ?? []}
          selected={value.categories ?? []}
          onToggle={(id) => toggleIn("categories", id)}
          disabled={disabled}
        />
        <MultiSelect
          label="State"
          options={groups?.states ?? []}
          selected={value.states ?? []}
          onToggle={(id) => toggleIn("states", id)}
          disabled={disabled}
        />
        <MultiSelect
          label="Committee"
          options={groups?.committees ?? []}
          selected={value.committees ?? []}
          onToggle={(id) => toggleIn("committees", id)}
          disabled={disabled}
        />
        <MultiSelect
          label="Upgrade diet (applicants)"
          options={groups?.upgrade_diets ?? []}
          selected={value.upgrade_diets ?? []}
          onToggle={(id) => toggleIn("upgrade_diets", id)}
          disabled={disabled}
          emptyHint="No upgrade review cycles yet."
        />
        {/* PAID candidates only. This is the list the exams office writes to
            about a sitting — centre, timetable, what to bring — and a draft
            nobody has paid for is not a candidate, so the label says which it
            is rather than leaving it to be discovered in the preview. */}
        <MultiSelect
          label="Exam diet (paid candidates)"
          options={groups?.exam_diets ?? []}
          selected={value.exam_diets ?? []}
          onToggle={(id) => toggleIn("exam_diets", id)}
          disabled={disabled}
          emptyHint="No exam diet is open at the moment."
        />
        {/* Everyone signed up for a live event — guests included, since they
            registered without an account and no other group reaches them. */}
        <MultiSelect
          label="Event registrants"
          options={groups?.events ?? []}
          selected={value.events ?? []}
          onToggle={(id) => toggleIn("events", id)}
          disabled={disabled}
          emptyHint="No published events at the moment."
        />

        {/* ── Which registrations ──────────────────────────────────────────
            The two lists the desk actually writes to separately: the people
            who will be admitted, and the people who still owe. Appears only
            once an event is ticked, because on its own it selects nobody.

            Default stays EVERYONE, which is what an event audience has always
            meant — a filter that applied itself would silently shrink a
            mail-out somebody had already composed. */}
        {(value.events ?? []).length > 0 ? (
          <div className="sm:col-span-2 rounded-xl border border-indigo-100 bg-indigo-50/50 p-3">
            <div className="text-[10px] font-black uppercase tracking-widest text-indigo-700">
              Which event registrations
            </div>

            <div className="mt-2 flex flex-wrap gap-1.5">
              <button
                type="button"
                disabled={disabled}
                onClick={() => onChange({ ...value, event_payment: undefined })}
                title="Everyone signed up, whether or not they have paid"
                className={`px-3 py-1.5 rounded-xl border text-xs font-bold transition disabled:opacity-50 ${
                  !value.event_payment
                    ? "bg-gray-900 text-white border-gray-900"
                    : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
                }`}
              >
                Everyone registered
              </button>

              {(
                groups?.event_payment_levels ?? [
                  { key: "paid" as const, label: "Paid registrations only", hint: "" },
                  { key: "unpaid" as const, label: "Unpaid registrations only", hint: "" },
                ]
              ).map((level) => (
                <button
                  key={level.key}
                  type="button"
                  disabled={disabled}
                  onClick={() => onChange({ ...value, event_payment: level.key })}
                  title={level.hint}
                  className={`px-3 py-1.5 rounded-xl border text-xs font-bold transition disabled:opacity-50 ${
                    value.event_payment === level.key
                      ? "bg-gray-900 text-white border-gray-900"
                      : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  {level.label}
                  {/* The head count for the chosen events, so the level is
                      picked knowing how many it reaches. */}
                  <span className="ml-1.5 text-[10px] font-black opacity-60 tabular-nums">
                    {eventLevelCount(level.key)}
                  </span>
                </button>
              ))}
            </div>

            <p className="mt-2 text-[11px] font-semibold text-gray-600">
              {value.event_payment === "paid"
                ? "Fee settled, or the event was free — the delegates who will be admitted and credited with CPD. Guests who have paid are included."
                : value.event_payment === "unpaid"
                  ? "Holding a place with the fee still outstanding. This is the chasing list."
                  : "Everyone signed up for the events ticked above, paid or not."}
            </p>
          </div>
        ) : null}
        <MultiSelect
          label="Executive set"
          options={groups?.executive_sets ?? []}
          selected={Array.isArray(value.executives) ? value.executives : []}
          onToggle={(id) => {
            const current = Array.isArray(value.executives) ? value.executives : [];
            const next = current.includes(id) ? current.filter((x) => x !== id) : [...current, id];
            onChange({ ...value, executives: next });
          }}
          disabled={disabled || executivesCurrent}
          emptyHint="No executive sets recorded."
        />
        {/* Individual seats on the National Policy Committee — "the trustees",
            "the past presidents" — for when the whole committee is too wide.
            Disabled while the one-tick card above already covers all of them. */}
        <MultiSelect
          label="Policy committee seat"
          options={groups?.national_positions ?? []}
          selected={Array.isArray(value.national_positions) ? value.national_positions : []}
          onToggle={(id) => {
            const current = Array.isArray(value.national_positions) ? value.national_positions : [];
            const next = current.includes(id) ? current.filter((x) => x !== id) : [...current, id];
            onChange({ ...value, national_positions: next });
          }}
          disabled={disabled || npcAll}
          emptyHint="No policy committee positions recorded yet."
        />
      </div>

      {/* Chapter chairmen for specific chapters, when not sending to all. */}
      {!chairmenAll && (
        <MultiSelect
          label="Chairman of specific chapters"
          options={groups?.chapters ?? []}
          selected={Array.isArray(value.chapter_chairmen) ? value.chapter_chairmen : []}
          onToggle={(id) => {
            const current = Array.isArray(value.chapter_chairmen) ? value.chapter_chairmen : [];
            const next = current.includes(id) ? current.filter((x) => x !== id) : [...current, id];
            onChange({ ...value, chapter_chairmen: next });
          }}
          disabled={disabled}
        />
      )}

      {/* Refinements. Everything above ADDS people; these two take them away —
          the panel is set apart and labelled so the difference is not something
          an admin has to infer from the head-count moving the wrong way. */}
      <div className="border border-gray-100 rounded-2xl overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
          <span className="text-[10px] font-black uppercase tracking-[0.15em] text-gray-500">
            Narrow the selection
          </span>
          <p className="text-[10px] text-gray-400 mt-0.5">
            Applied to everyone picked above. Leave blank to send to all of them.
          </p>
        </div>

        <div className="grid sm:grid-cols-3 gap-3 p-4">
          <label className="block">
            <span className="text-[10px] font-black uppercase tracking-[0.15em] text-gray-400">
              Financial status
            </span>
            <select
              value={value.dues ?? ""}
              disabled={disabled}
              onChange={(e) => onChange({ ...value, dues: e.target.value })}
              className="mt-1.5 w-full border border-gray-200 rounded-xl px-3 py-2 text-xs bg-white outline-none focus:border-gray-900"
            >
              <option value="">Any standing</option>
              <option value="up_to_date">Financially up to date</option>
              <option value="owing">Not up to date (owing dues)</option>
            </select>
          </label>

          <label className="block">
            <span className="text-[10px] font-black uppercase tracking-[0.15em] text-gray-400">
              Registered from
            </span>
            <input
              type="date"
              value={value.reg_from ?? ""}
              disabled={disabled}
              onChange={(e) => onChange({ ...value, reg_from: e.target.value })}
              className="mt-1.5 w-full border border-gray-200 rounded-xl px-3 py-2 text-xs bg-white outline-none focus:border-gray-900"
            />
          </label>

          <label className="block">
            <span className="text-[10px] font-black uppercase tracking-[0.15em] text-gray-400">
              Registered to
            </span>
            <input
              type="date"
              value={value.reg_to ?? ""}
              disabled={disabled}
              onChange={(e) => onChange({ ...value, reg_to: e.target.value })}
              className="mt-1.5 w-full border border-gray-200 rounded-xl px-3 py-2 text-xs bg-white outline-none focus:border-gray-900"
            />
          </label>
        </div>

        {(value.dues || value.reg_from || value.reg_to) && (
          <p className="text-[10px] text-gray-500 bg-gray-50 border-t border-gray-100 px-4 py-2.5">
            While a filter is on, anyone without a member record — event guests
            and the extra addresses below — is left out, because there is no
            record on which the filter can be shown to hold.
          </p>
        )}
      </div>

      <div>
        <label className="text-[10px] font-black uppercase tracking-[0.15em] text-gray-500">
          Extra addresses
        </label>
        <textarea
          value={emailText}
          disabled={disabled}
          onChange={(e) => commitEmails(e.target.value)}
          placeholder="One address per line — for anyone not on the register."
          className="mt-2 w-full border border-gray-200 rounded-2xl px-4 py-3 text-xs outline-none focus:border-gray-900 min-h-[72px] placeholder:text-gray-300"
        />
      </div>

      {/* Resolved head-count */}
      <div className="bg-gray-50 border border-gray-100 rounded-2xl p-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-gray-900 flex items-center justify-center text-white">
            {counting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Users className="w-4 h-4" />
            )}
          </div>
          <div>
            <p className="text-lg font-black text-gray-900 leading-none">
              {counting ? "…" : (summary?.total ?? 0)}
            </p>
            <p className="text-[10px] text-gray-400 font-medium mt-1">
              recipient(s), duplicates removed
            </p>
          </div>

          {/* Matched but unmailable. Shown so a head-count that looks short is
              explained rather than doubted. */}
          {!!summary?.skipped && (
            <div className="ml-auto text-right">
              <p className="text-sm font-black text-amber-600 leading-none">{summary.skipped}</p>
              <p className="text-[10px] text-gray-400 font-medium mt-1">skipped</p>
            </div>
          )}
        </div>

        {!!summary?.skipped && (
          <p className="text-[10px] text-amber-700 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2 mt-3">
            {Object.entries(summary.skipped_reasons)
              .map(([reason, count]) => `${count} ${reason.toLowerCase()}`)
              .join(", ")}{" "}
            — matched by a group above, but left out of the send.
          </p>
        )}

        {summary && summary.total > 0 && (
          <>
            <div className="flex flex-wrap gap-1.5 mt-4">
              {Object.entries(summary.by_group).map(([group, count]) => (
                <span
                  key={group}
                  className="inline-flex items-center gap-1.5 bg-white border border-gray-200 rounded-lg px-2.5 py-1 text-[10px] font-bold text-gray-600"
                >
                  {group}
                  <span className="text-indigo-600">{count}</span>
                </span>
              ))}
            </div>

            <details className="mt-3">
              <summary className="text-[10px] font-black uppercase tracking-[0.15em] text-gray-400 cursor-pointer hover:text-gray-600">
                Preview first {summary.sample.length}
              </summary>
              <ul className="mt-2 space-y-1">
                {summary.sample.map((r) => (
                  <li key={r.email} className="text-[11px] text-gray-500">
                    <span className="font-bold text-gray-700">{r.name ?? "—"}</span> · {r.email}
                  </li>
                ))}
              </ul>
            </details>
          </>
        )}

        {!counting && audienceIsEmpty(value) && (
          <p className="text-[11px] text-gray-400 mt-3 flex items-center gap-1.5">
            <X className="w-3 h-3" /> No group selected yet.
          </p>
        )}
      </div>
    </div>
  );
}
